import { Select } from "../ui/select.tsx";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { PlusIcon } from "@phosphor-icons/react";
import { q } from "../api.ts";
import { DataSources } from "../data-settings.tsx";
import { Diagnosis } from "../diagnosis.tsx";
import { Ledger } from "../ledger.tsx";
import { AREA_LABELS as A, DATA_PAGE_LABELS as D, DIAGNOSIS_LABELS as X, LEDGER_LABELS as G, METRIC_DASHBOARD_LABELS as M, METRIC_LABELS as L } from "../labels.ts";
import { type DataSearch as Search, type DataView as View, type Grain, WEEKS, type Weeks } from "../metric-dashboard.ts";
import { MetricDefinitions, Metrics } from "../metrics.tsx";
import { MetricWorkbench } from "../metric-workbench.tsx";
import { Button, ErrorText, Page, TabPanel, Tabs, ViewSwitch } from "../ui.tsx";
import { Skeleton } from "../ui/skeleton.tsx";

// Insights (docs/design-system.md §0.2, §0.4f, plan M7 step 7i): where the
// product stands at each growth stage (the default tab), its metrics and
// where they come from. Opportunities are not listed here: a person meets
// relevant discussions in a campaign's table and in the inbox.

/**
 * `/p/:product/data` opens the diagnosis, `?view=metrics` the metrics (they
 * keep their range and grain, `&weeks=12&grain=day`, in the address),
 * `?view=sources` the data sources (the retired `?view=opportunities` does
 * too) and `?view=money` the costs and revenue (`&add=cost` opens a new entry).
 */
export { dataSearch } from "../metric-dashboard.ts";

export function DataPage() {
  const navigate = useNavigate();
  const { productId } = useParams({ strict: false }) as { productId: string };
  const search = useSearch({ strict: false }) as Search;
  const products = useQuery(q.products);
  const product = products.data?.find((candidate) => candidate.id === productId);
  const view: View = search.view ?? "diagnosis";
  const weeks: Weeks = search.weeks === 12 ? "12" : "4", grain: Grain = search.grain ?? "week";
  const crumbs = [{ label: product?.name ?? A.product, to: "/p/$productId/week", params: { productId } }, { label: A.data }];
  if (products.isLoading) return <Page crumbs={crumbs} title={A.data}><Skeleton shape="stats" /></Page>;
  if (!product) return <Page crumbs={crumbs} title={A.data}><ErrorText error={products.error} /></Page>;
  const go = (next: Search, push = false) => { void navigate({ to: "/p/$productId/data", params: { productId }, search: next, replace: !push }); };
  const setView = (next: View) => go(next === "diagnosis" ? {} : { view: next });
  const setCreating = (open: boolean) => { const { create: _create, ...rest } = search; go({ ...rest, ...(open ? { create: "metric" } : {}) }); };
  const setRange = (next: { weeks?: Weeks; grain?: Grain }) => {
    const merged = { weeks, grain, ...next };
    go({ view: "metrics", ...(merged.weeks === "12" ? { weeks: 12 } : {}), ...(merged.grain === "day" ? { grain: "day" } : {}) });
  };
  const controls = view === "metrics" && <>
    <Select label={M.range} value={weeks} onValueChange={(nextValue) => setRange({ weeks: nextValue as Weeks })} options={WEEKS.map((value) => ({ value: value, label: M.weeks[value] }))} />
    <ViewSwitch label={M.grain} value={grain} onValueChange={(next) => setRange({ grain: next })} options={[{ value: "week", label: M.grains.week }, { value: "day", label: M.grains.day }]} />
  </>;
  return <Page wide crumbs={crumbs} title={A.data} actions={controls}>
    <MetricWorkbench product={product} weeks={weeks} grain={grain} search={search} onSearch={go}><div className="z-data-views"><Tabs value={view} onValueChange={setView} tabs={[{ value: "diagnosis", label: X.tab }, { value: "metrics", label: D.metrics }, { value: "definitions", label: M.definitions }, { value: "sources", label: D.sources }, { value: "money", label: G.tab }]} tools={(view === "metrics" || view === "definitions") && <Button tone="primary" onClick={() => setCreating(true)}><PlusIcon size={16} aria-hidden />{L.add}</Button>}>
      <TabPanel value="diagnosis">{view === "diagnosis" && <Diagnosis productId={product.id} />}</TabPanel>
      <TabPanel value="metrics">{view === "metrics" && <Metrics product={product} weeks={weeks} grain={grain} onCreate={() => setCreating(true)} />}</TabPanel>
      <TabPanel value="definitions">{view === "definitions" && <MetricDefinitions product={product} />}</TabPanel>
      <TabPanel value="sources">{view === "sources" && <DataSources product={product} />}</TabPanel>
      <TabPanel value="money">{view === "money" && <Ledger product={product} adding={search.add ?? null} onAdding={(add) => go({ view: "money", ...(add ? { add } : {}) })} />}</TabPanel>
    </Tabs></div>
    </MetricWorkbench>
  </Page>;
}
